import React, { useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Slider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import * as d3 from 'd3';
import PriceHistogram from './PriceHistogram';

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
}));

const FiltersDialog = ({
    open,
    onClose,
    priceRange,
    onPriceChange,
    sortOrder,
    onSortOrderChange,
    priceData,         // filtered prices (for the histogram’s density)
    originalPriceData, // original search prices (for slider limits)
    onApplyFilters,
    onClearFilters,
}) => {
    const handleApply = () => {
        onApplyFilters();
    };

    // Compute slider max based on original search results.
    const sliderMax = useMemo(() => {
        if (!originalPriceData || originalPriceData.length === 0) return 1000;
        return Math.ceil(d3.max(originalPriceData) * 1.1);
    }, [originalPriceData]);

    // fixedDomain for histogram (always from 0 to sliderMax)
    const fixedDomain = useMemo(() => [0, sliderMax], [sliderMax]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <StyledDialogTitle>
                <Typography variant="h6">Filters & Sorting</Typography>
            </StyledDialogTitle>
            <DialogContent>
                {/* Histogram (centered) */}
                {priceData && priceData.length > 0 && (
                    <Box mb={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box>
                            <PriceHistogram
                                width={360}
                                height={200}
                                data={priceData}
                                fixedDomain={fixedDomain}
                            />
                        </Box>
                    </Box>
                )}

                {/* Price Range Slider */}
                <Box mb={3}>
                    <Typography variant="body1" gutterBottom>
                        Price Range (USD)
                    </Typography>
                    <Slider
                        value={priceRange}
                        onChange={onPriceChange}
                        valueLabelDisplay="auto"
                        min={0}
                        max={sliderMax}
                    />
                    <Box display="flex" justifyContent="space-between" mt={1}>
                        <Typography variant="caption">0 USD</Typography>
                        <Typography variant="caption">{sliderMax} USD</Typography>
                    </Box>
                </Box>

                {/* Sorting Control */}
                <Box mb={3}>
                    <FormControl fullWidth>
                        <InputLabel id="sort-order-label">Sort</InputLabel>
                        <Select
                            labelId="sort-order-label"
                            value={sortOrder}
                            onChange={onSortOrderChange}
                            label="Sort"
                        >
                            <MenuItem value="relevance">Relevance</MenuItem>
                            <MenuItem value="asc">Price: Low to High</MenuItem>
                            <MenuItem value="desc">Price: High to Low</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClearFilters} variant="outlined">
                    Clear All
                </Button>
                <Button onClick={handleApply} variant="contained" color="primary">
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

FiltersDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    priceRange: PropTypes.array.isRequired,
    onPriceChange: PropTypes.func.isRequired,
    sortOrder: PropTypes.oneOf(['relevance', 'asc', 'desc']).isRequired,
    onSortOrderChange: PropTypes.func.isRequired,
    priceData: PropTypes.arrayOf(PropTypes.number).isRequired,
    originalPriceData: PropTypes.arrayOf(PropTypes.number).isRequired,
    onApplyFilters: PropTypes.func.isRequired,
    onClearFilters: PropTypes.func.isRequired,
};

export default FiltersDialog;
